<!-- AddUserGroupForm.vue -->
<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import { useGroupStore } from "@/stores/useGroupStore";
import { useUserGroupStore } from "@/stores/useUserGroupStore";
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

import SearchUserInput from "./SearchUserInput.vue";
import SearchGroupInput from "./SearchGroupInput.vue";
import ThePagination from "../Generics/ThePagination.vue";
import AddTextLimitModal from "@/components/Text/AddTextLimitModal.vue";
import UpgradePage from "@/components/User/UpgradePage.vue";

const emit = defineEmits(["close", "refresh"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const userStore = useUserStore();
const groupStore = useGroupStore();
const userGroupStore = useUserGroupStore();

const selectedUserId = ref(null);
const selectedGroupId = ref(null);

// Remove o usuário logado da lista exibida no modal
const visibleUsers = computed(() => {
  const loggedUserId =
    userStore.currentUser?.userId ?? userStore.currentUser?.id ?? null;

  if (!loggedUserId) return userStore.users;

  return userStore.users.filter((user) => user.userId !== loggedUserId);
});

function normalizeAvatarPath(path) {
  if (!path) return "";

  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}

function resolveUserPhotoPath(user) {
  if (!user) return null;

  return (
    user.photoPath ||
    user.photoUrl ||
    user.userPhotoPath ||
    user.userPhotoUrl ||
    user.photo_path ||
    user.user_photo_path ||
    null
  );
}

const isSubmitting = ref(false);
const apiError = ref(null);

const userCurrentPage = ref(1);
const groupCurrentPage = ref(1);
const pageSize = ref(10);

// TOAST
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

// Ao concluir com sucesso, esconde só o painel do modal; o toast (num Teleport
// próprio) segue visível sozinho até o emit("close") desmontar o componente.
const hideBackdropForSuccess = ref(false);

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
  }, 3000);
}

async function fetchPaginatedUsers() {
  await userStore.fetchUsers(
    userCurrentPage.value,
    pageSize.value,
    userStore.userSearchTerm
  );
}

async function fetchPaginatedGroups() {
  await groupStore.fetchGroupsPaged(
    groupCurrentPage.value,
    pageSize.value,
    groupStore.groupSearchTerm
  );
}

onMounted(async () => {
  await Promise.all([fetchPaginatedUsers(), fetchPaginatedGroups()]);
});

onBeforeUnmount(() => {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

watch(userCurrentPage, fetchPaginatedUsers);
watch(groupCurrentPage, fetchPaginatedGroups);

watch(
  () => userStore.userSearchTerm,
  () => {
    userCurrentPage.value = 1;
    fetchPaginatedUsers();
  }
);

watch(
  () => groupStore.groupSearchTerm,
  () => {
    groupCurrentPage.value = 1;
    fetchPaginatedGroups();
  }
);

// MODAL DE LIMITE
const showLimitModal = ref(false);
const limitModalInfo = ref({
  limit: null,
  currentUsage: null,
  newUsage: null,
  message: "",
});

// ✅ MODAL DE UPGRADE (Padrão do BulkCartesianForm)
const showUpgradeModal = ref(false);

function handleCloseLimitModal() {
  showLimitModal.value = false;
}

function handleOpenPlans() {
  showLimitModal.value = false;
  showUpgradeModal.value = true;
}

function handleCloseUpgrade() {
  showUpgradeModal.value = false;
}

async function handleSubmit() {
  if (isSubmitting.value) return;

  if (!selectedUserId.value || !selectedGroupId.value) {
    showErrorToast("Selecione um usuário e um grupo para continuar.");
    return;
  }

  isSubmitting.value = true;
  apiError.value = null;

  try {
    await userGroupStore.createAssociation({
      userId: selectedUserId.value,
      groupId: selectedGroupId.value,
    });

    showSuccessToast("Usuário adicionado ao grupo com sucesso!");
    hideBackdropForSuccess.value = true;

    setTimeout(() => {
      emit("refresh");
      emit("close");
    }, 1200);
  } catch (error) {
    console.error("Erro ao criar vínculo:", error);

    const backendData = error?.response?.data;
    let errorMessage =
      backendData?.detail ||
      backendData?.title ||
      backendData?.message ||
      error?.message ||
      "Ocorreu um erro ao adicionar um usuário ao grupo.";

    const isLimitError =
      errorMessage.includes("plano atual permite associar") ||
      errorMessage.includes("Nenhum plano ativo encontrado");

    if (isLimitError) {
      limitModalInfo.value = {
        limit: backendData?.limit ?? null,
        currentUsage: backendData?.currentUsage ?? null,
        newUsage: backendData?.newUsage ?? null,
        message: errorMessage,
      };

      isSubmitting.value = false;
      showLimitModal.value = true;
    } else {
      if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMessage = errors[0] || errorMessage;
      }

      showErrorToast(errorMessage);
      isSubmitting.value = false;
    }
  }
}
</script>

<template>
  <div v-if="!showUpgradeModal && !hideBackdropForSuccess" class="modal-backdrop">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">Adicionar Usuário a Grupo</h2>

        <button
          @click="emit('close')"
          class="modal-close-btn"
          aria-label="Fechar"
          type="button"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <p class="modal-description">
          Selecione uma usuário e um grupo para criar a associação.
        </p>

        <!-- ✅ apertado verticalmente (mesmo approach do BulkCartesianForm) -->
        <div
          class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3"
          style="height: 540px"
        >
          <div class="list-container">
            <div class="list-title">
              <h3>Usuários</h3>
            </div>

            <div class="search-input-wrapper">
              <SearchUserInput />
            </div>

            <div class="list-scroll">
              <div
                v-for="user in visibleUsers"
                :key="user.userId"
                class="list-item"
                :class="{ selected: selectedUserId === user.userId }"
                @click="selectedUserId = user.userId"
              >
                <input
                  type="radio"
                  :id="`user-${user.userId}`"
                  :value="user.userId"
                  v-model="selectedUserId"
                  name="user-selection"
                />
                <label :for="`user-${user.userId}`" class="user-option-label">
                  <span class="user-avatar">
                    <span
                      v-if="getAvatarUrl(resolveUserPhotoPath(user))"
                      class="user-avatar-img"
                      :style="{
                        backgroundImage: `url('${getAvatarUrl(
                          resolveUserPhotoPath(user)
                        )}')`,
                      }"
                    ></span>

                    <i v-else class="fas fa-user user-avatar-icon"></i>
                  </span>

                  <span class="user-option-text">
                    <span class="user-option-name">{{ user.userName }}</span>
                    <span class="user-option-email">({{ user.email }})</span>
                  </span>
                </label>
              </div>
            </div>

            <ThePagination
              class="pagination-tight border-t"
              :current-page="userCurrentPage"
              :page-size="pageSize"
              :total-count="userStore.totalUsersCount"
              :total-pages="userStore.totalUsersPages"
              @change-page="(page) => (userCurrentPage = page)"
            />
          </div>

          <div class="list-container">
            <div class="list-title">
              <h3>Grupos</h3>
            </div>

            <div class="search-input-wrapper">
              <SearchGroupInput />
            </div>

            <div class="list-scroll">
              <div
                v-for="group in groupStore.groups"
                :key="group.groupId"
                class="list-item"
                :class="{ selected: selectedGroupId === group.groupId }"
                @click="selectedGroupId = group.groupId"
              >
                <input
                  type="radio"
                  :id="`group-${group.groupId}`"
                  :value="group.groupId"
                  v-model="selectedGroupId"
                  name="group-selection"
                />
                <label :for="`group-${group.groupId}`">
                  {{ group.groupName }}
                </label>
              </div>
            </div>

            <ThePagination
              class="pagination-tight border-t"
              :current-page="groupCurrentPage"
              :page-size="pageSize"
              :total-count="groupStore.totalGroupsCount"
              :total-pages="groupStore.totalGroupsPages"
              @change-page="(page) => (groupCurrentPage = page)"
            />
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button
          @click="emit('close')"
          class="btn btn-secondary"
          :disabled="isSubmitting"
          type="button"
        >
          Cancelar
        </button>

        <button
          @click="handleSubmit"
          class="btn btn-success"
          :disabled="isSubmitting"
          type="button"
        >
          <svg
            v-if="!isSubmitting"
            class="w-5 h-5 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
              clip-rule="evenodd"
            />
          </svg>

          <svg
            v-else
            class="w-5 h-5 mr-2 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>

          {{ isSubmitting ? "Adicionando..." : "Adicionar ao Grupo" }}
        </button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>

  <Teleport to="body">
    <AddTextLimitModal
      :show="showLimitModal"
      :info="limitModalInfo"
      entityType="usuários"
      @close="handleCloseLimitModal"
      @open-plans="handleOpenPlans"
    />
  </Teleport>

  <Teleport to="body">
    <UpgradePage
      v-if="showUpgradeModal"
      :initial-step="2"
      @close="handleCloseUpgrade"
    />
  </Teleport>
</template>

<style scoped>

.modal-panel {
  max-width: 62.5rem;
  height: calc(100vh - 14px - 1.5rem);
  max-height: calc(100vh - 14px - 1.5rem);
}

.modal-description {
  color: var(--text-muted);
  margin: 0;
  font-size: 0.92rem; /* ↓ era 0.95rem */
}

.modal-footer {
  bottom: 0;
}

/* =========================
   LISTAS
========================= */
.list-container {
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  overflow: hidden;
  background: var(--surface);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.list-title {
  padding: 0.45rem 0.875rem; /* ↓ era 0.5rem */
  background-color: var(--bg-header);
  font-weight: 600;
  font-size: 0.92rem; /* ↓ era 0.95rem */
  border-bottom: 1px solid var(--border-color);
}

.list-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.4rem; /* ↓ era 0.5rem */
}

.list-item {
  display: flex;
  align-items: center;
  padding: 0.32rem 0.5rem; /* ↓ */
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.2rem; /* ↓ */
  font-size: 0.9rem; /* ↓ */
}

.list-item:hover {
  background-color: var(--surface-2);
}

.list-item.selected {
  background-color: #ecfdf3;
  border-left: 4px solid var(--success);
  font-weight: 500;
  color: var(--success-text);
}

.list-item input[type="radio"] {
  display: none;
}

.list-item label {
  cursor: pointer;
  width: 100%;
  user-select: none;
}

/* =========================
   PAGINAÇÃO (mesmo padrão do BulkCartesianForm)
========================= */
.pagination-tight {
  padding: 6px !important;
  margin: 6px !important;
}

/* desloca texto + placeholder */
.search-input-wrapper :deep(.search-input) {
  padding-left: 2.1rem;
}

/* desloca o ícone junto */
.search-input-wrapper :deep(.icon-wrapper) {
  padding-left: 1.2rem;
}
/* =========================
   AVATAR NA LISTA DE USUÁRIOS
========================= */
.user-option-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  min-width: 0;
}

.user-avatar {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background-color: var(--border-color);
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 26px;
}

.user-avatar-img {
  width: 100%;
  height: 100%;
  display: block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.user-avatar-icon {
  font-size: 0.75rem;
  line-height: 1;
}

.user-option-text {
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  min-width: 0;
  overflow: hidden;
}

.user-option-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-option-email {
  color: var(--text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
